/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useDispatch } from "react-redux";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import CardContent from "@material-ui/core/CardContent";

import {
  SimpleShowLayout,
  WithReduxForm,
  PageHeader,
  TextField,
  FunctionField,
  TextInput,
  BooleanInput,
  SelectInput,
  showNotification,
  PageFooter,
  BackButton,
  Button,
  required as jsRequired,
} from "jazasoft";
import { required } from "@jazasoft/mui-table";

// material-ui
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import { Divider, Grid } from "@material-ui/core";

// helper
import useQuery from "../../../hooks/useQuery";
import { isEmpty, isTrue } from "../../../utils/helpers";
import useSafeState from "../../../hooks/useSafeState";
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";
import useMutation from "../../../hooks/useMutation";
import useDeepMemo from "../../../hooks/useDeepMemo";
import { choicesApiOperation, choicesFileOperation, choicesHttpMethod, choicesFileType } from "./constants";

const useStyles = makeStyles(() => ({
  content: {
    margin: "1.5em",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.75em",
  },
  mrTop: {
    marginTop: "1.5em",
  },
}));

const StyleView = props => {
  const classes = useStyles();
  const { id, i18nKey, resource } = props;

  const [version, setVersion] = useSafeState(1);
  const [editing, setEditing] = useSafeState(false);

  // hooks
  const dispatch = useDispatch();
  const [mutate, { loading }] = useMutation();

  const { data: externalApp } = useQuery({ url: `externalApps/${id}`, version });

  const onSubmit = handleSubmit => {
    handleSubmit(values => {
      const data = { ...externalApp, ...values };

      let options = {
        url: `${resource}/${id}`,
        method: "put",
        data,
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("App details updated successfully."));
          setEditing(false);
          setVersion(version => version + 1);
        },
      });
    })();
  };

  const onOperationSubmit = (values, _, onSubmitComplete) => {
    const data = { ...externalApp, integrationOperationList: values };

    let options = {
      url: `${resource}/${id}`,
      method: "put",
      data,
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Operation details updated successfully."));
        setVersion(version => version + 1);
        onSubmitComplete && onSubmitComplete();
      },
    });
  };

  const columns = useDeepMemo(() => {
    const integrationType = externalApp?.integrationType;
    let columnList = [
      {
        dataKey: "entityName",
        title: "Entity Name",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "entityId",
        title: "Entity Id",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "entitySerial",
        title: "Entity Serial",
        inputType: "text-input",
        options: { type: "number" },
      },
      {
        dataKey: "operation",
        title: "Operation",
        inputType: "select-input",
        choices: integrationType === "API" ? choicesApiOperation : choicesFileOperation,
        validate: [required()],
      },
      {
        dataKey: "schedule",
        title: "Schedule",
        inputType: "text-input",
      },
      {
        dataKey: "meta",
        title: "Metadata",
        inputType: "text-input",
      },
      {
        dataKey: "enabled",
        title: "Enabled",
        inputType: "boolean-input",
        render: value => (isTrue(value) ? "Yes" : "No"),
      },
      {
        dataKey: "url",
        title: "URL",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "method",
        title: "Http Method",
        inputType: "select-input",
        choices: choicesHttpMethod,
        validate: [required()],
      },
      {
        dataKey: "headers",
        title: "Http Headers",
        inputType: "text-input",
      },
      {
        dataKey: "params",
        title: "Http Params",
        inputType: "text-input",
      },
      {
        dataKey: "authConfig",
        title: "Auth Config",
        inputType: "text-input",
      },
      {
        dataKey: "fileType",
        title: "File Type",
        inputType: "select-input",
        choices: choicesFileType,
        validate: [required()],
      },
      {
        dataKey: "location",
        title: "File Location",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "regexp",
        title: "Regexp (for filtering file)",
        inputType: "text-input",
      },
    ];
    const fileColumns = ["fileType", "location", "regexp"];
    const apiColumns = ["authConfig", "url", "method", "headers", "params"];
    if (integrationType === "API") {
      columnList = columnList.filter(c => !fileColumns.includes(c.dataKey));
    } else {
      columnList = columnList.filter(c => !apiColumns.includes(c.dataKey));
    }
    return columnList;
  }, [externalApp]);

  const rows = useDeepMemo(() => {
    if (isEmpty(externalApp?.integrationOperationList)) return [];
    let rowList = externalApp.integrationOperationList.sort((a, b) => a.entitySerial - b.entitySerial);

    return rowList;
  }, [externalApp]);

  return (
    <div className={classes.root}>
      <PageHeader title={`Registered App #${id}`} />

      <div className={classes.content}>
        <WithReduxForm initialValues={externalApp}>
          {({ handleSubmit }) => (
            <Card className={classes.mrTop}>
              <CardHeader title="Basic Details" />
              <Divider />

              <CardContent>
                {editing && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextInput source="name" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextInput source="appId" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <SelectInput
                        source="integrationType"
                        i18nKey={i18nKey}
                        validate={[jsRequired()]}
                        choices={[
                          { id: "API", name: "API" },
                          { id: "FILE", name: "FILE" },
                        ]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center" }}>
                      <BooleanInput source="enabled" i18nKey={i18nKey} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextInput source="serviceClass" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextInput source="authServiceClass" i18nKey={i18nKey} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput source="authConfig" i18nKey={i18nKey} fullWidth />
                    </Grid>
                  </Grid>
                )}
                {!editing && (
                  <SimpleShowLayout style={{ paddingTop: "1em" }} record={externalApp} i18nKey={i18nKey} footer={false}>
                    <TextField source="name" xs={12} sm={3} />
                    <TextField label="App Id" source="appId" xs={12} sm={3} />
                    <TextField source="integrationType" xs={12} sm={3} />
                    <FunctionField
                      source="enabled"
                      render={record => (isTrue(record?.enabled) ? "Yes" : "No")}
                      xs={12}
                      sm={3}
                    />
                    <TextField source="serviceClass" xs={12} sm={6} fullWidth />
                    <TextField source="authServiceClass" xs={12} sm={6} fullWidth />
                    <TextField source="authConfig" xs={12} sm={12} fullWidth />
                  </SimpleShowLayout>
                )}
              </CardContent>
              <Divider />
              <div className={classes.footer}>
                {!editing && (
                  <Button label="Edit" variant="outlined" color="primary" onClick={() => setEditing(true)} />
                )}
                {editing && (
                  <Button
                    label="Cancel"
                    variant="outlined"
                    color="primary"
                    onClick={() => setEditing(false)}
                    style={{ marginRight: "1em" }}
                  />
                )}
                {editing && (
                  <Button
                    label="Save"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={() => onSubmit(handleSubmit)}
                  />
                )}
              </div>
            </Card>
          )}
        </WithReduxForm>

        <Card className={classes.mrTop}>
          <CardHeader title="Operations" />
          <Divider />

          <PerfectScrollbar>
            <MuiTable
              columns={columns}
              rows={rows}
              editable
              enableRowAddition
              showEditableActions
              footerActions={[{ name: "edit", tooltip: "Edit", options: { variant: "outlined", color: "primary" } }]}
              rowAddCount={1}
              onRowAdd={(rows, rowIdx) => {
                const rowData = !isEmpty(rowIdx) ? rows[rowIdx] : rows[rows?.length - 1];
                const { id, ...row } = rowData;
                return row;
              }}
              onSubmit={onOperationSubmit}
              fontSize={16}
            />
          </PerfectScrollbar>
        </Card>

        <PageFooter>
          <BackButton variant="outlined" color="primary" />
        </PageFooter>
      </div>
    </div>
  );
};

export default StyleView;
